import posthog, { posthogEnabled } from "./posthog";

let installed = false;

// 绝不能把这些头的值发给 PostHog（JWT / Cookie 等敏感凭证）。
const SENSITIVE_HEADER_KEYS = new Set(["authorization", "cookie", "set-cookie", "x-api-key"]);
const MAX_BODY_PREVIEW_LENGTH = 2000;
// 同一个 "方法 + URL + 状态码/错误名" 组合在这个时间窗口内只上报一次，
// 防止某个接口持续 403/断网时被轮询请求刷爆 PostHog 配额。
const DEDUPE_WINDOW_MS = 30000;

const recentReports = new Map();

function shouldReport(dedupeKey) {
    const now = Date.now();
    const last = recentReports.get(dedupeKey);
    if (last && now - last < DEDUPE_WINDOW_MS) {
        return false;
    }
    recentReports.set(dedupeKey, now);
    // 定期清理，避免 Map 无限增长。
    if (recentReports.size > 200) {
        for (const [key, ts] of recentReports) {
            if (now - ts > DEDUPE_WINDOW_MS) {
                recentReports.delete(key);
            }
        }
    }
    return true;
}

function safeCaptureException(error, properties) {
    if (!posthogEnabled || typeof posthog.captureException !== "function") {
        return;
    }
    try {
        posthog.captureException(error, properties);
    } catch {
        // 上报本身失败时保持静默，绝不影响业务逻辑与原生控制台输出。
    }
}

function redactHeaderValue(key, value) {
    return SENSITIVE_HEADER_KEYS.has(String(key).toLowerCase()) ? "[redacted]" : value;
}

// 兼容 Headers 实例 / 数组对 / 普通对象三种 fetch 允许传入的 headers 形态。
function headersToObject(headers) {
    const result = {};
    if (!headers) {
        return result;
    }
    try {
        if (typeof Headers !== "undefined" && headers instanceof Headers) {
            headers.forEach((value, key) => {
                result[key] = redactHeaderValue(key, value);
            });
            return result;
        }
        if (Array.isArray(headers)) {
            headers.forEach((pair) => {
                if (Array.isArray(pair) && pair[0]) {
                    result[pair[0]] = redactHeaderValue(pair[0], pair[1]);
                }
            });
            return result;
        }
        if (typeof headers === "object") {
            Object.entries(headers).forEach(([key, value]) => {
                result[key] = redactHeaderValue(key, value);
            });
        }
    } catch {
        // 忽略无法解析的 headers 形态，不影响主流程。
    }
    return result;
}

function truncate(text, max = MAX_BODY_PREVIEW_LENGTH) {
    if (typeof text !== "string") {
        return text;
    }
    return text.length > max ? `${text.slice(0, max)}…[截断，原长度 ${text.length}]` : text;
}

function safeStringifyRequestBody(body) {
    if (body == null) {
        return undefined;
    }
    if (typeof body === "string") {
        return truncate(body);
    }
    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
        return truncate(body.toString());
    }
    if (typeof FormData !== "undefined" && body instanceof FormData) {
        // 只记录字段名，不记录值（可能含文件/敏感信息）。
        const keys = [];
        try {
            for (const [key] of body.entries()) {
                keys.push(key);
            }
        } catch {
            // ignore
        }
        return `FormData(${keys.join(", ")})`;
    }
    if (typeof Blob !== "undefined" && body instanceof Blob) {
        return `[Blob ${body.type || "unknown"}, ${body.size} bytes]`;
    }
    try {
        return truncate(JSON.stringify(body));
    } catch {
        return "[无法序列化的请求体]";
    }
}

// 汇总一次 fetch 调用的请求信息：URL、方法、头、body，兼容 Request 对象与 (url, init) 两种调用方式。
function getRequestMeta(input, init) {
    let url = "";
    let method = "GET";
    let headers = {};
    let credentials;
    let mode;

    if (typeof Request !== "undefined" && input instanceof Request) {
        url = input.url;
        method = input.method || "GET";
        headers = headersToObject(input.headers);
        credentials = input.credentials;
        mode = input.mode;
    } else {
        try {
            url = typeof input === "string" ? input : input?.url || String(input);
        } catch {
            url = "";
        }
    }

    let body;
    if (init) {
        if (init.method) {
            method = init.method;
        }
        if (init.headers) {
            headers = { ...headers, ...headersToObject(init.headers) };
        }
        if (init.credentials) {
            credentials = init.credentials;
        }
        if (init.mode) {
            mode = init.mode;
        }
        if ("body" in init) {
            body = safeStringifyRequestBody(init.body);
        }
    }

    return { url, method: String(method).toUpperCase(), headers, body, credentials, mode };
}

function isCrossOrigin(url) {
    try {
        return new URL(url, window.location.href).origin !== window.location.origin;
    } catch {
        return false;
    }
}

// 跨域请求失败（尤其是 CORS 预检被拒）时，浏览器出于安全考虑不会把真实状态码/响应头暴露给 JS，
// fetch() 只会 reject 一个语焉不详的 TypeError: Failed to fetch。
// Resource Timing API 是少数能绕过这个限制拿到线索的地方：只要服务端返回了 Timing-Allow-Origin，
// 就能读到 responseStatus / transferSize / 各阶段耗时，即使那次请求本身被 CORS 挡下。
function getResourceTimingHint(url) {
    try {
        if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") {
            return null;
        }
        const entries = performance.getEntriesByName(url).length
            ? performance.getEntriesByName(url)
            : performance.getEntriesByType("resource").filter((entry) => entry.name === url);
        const entry = entries[entries.length - 1];
        if (!entry) {
            return null;
        }
        return {
            responseStatus: entry.responseStatus || null,
            transferSize: entry.transferSize ?? null,
            encodedBodySize: entry.encodedBodySize ?? null,
            nextHopProtocol: entry.nextHopProtocol || null,
            durationMs: Math.round(entry.duration),
            serverTiming: (entry.serverTiming || []).map((t) => ({ name: t.name, duration: t.duration, description: t.description })),
        };
    } catch {
        return null;
    }
}

function classifyHttpError(status) {
    if (status === 401) {
        return "unauthorized";
    }
    if (status === 403) {
        return "forbidden_or_cors";
    }
    if (status === 404) {
        return "not_found";
    }
    if (status === 429) {
        return "rate_limited";
    }
    if (status >= 500) {
        return "server_error";
    }
    if (status >= 400) {
        return "client_error";
    }
    return "unknown";
}

// 读取失败响应的详细信息（状态、响应头、响应体预览）。用 clone() 读取，绝不消费原始 body，
// 保证各处调用方自己的 response.json()/text() 逻辑完全不受影响。
async function buildResponseDetails(response) {
    const headers = {};
    try {
        response.headers.forEach((value, key) => {
            headers[key] = redactHeaderValue(key, value);
        });
    } catch {
        // ignore
    }

    let body;
    const contentLength = Number(response.headers.get("content-length") || 0);
    const contentType = response.headers.get("content-type") || "";
    const looksTextual = !contentType || /json|text|xml|html|javascript/i.test(contentType);

    if (!looksTextual) {
        body = `[非文本响应体: ${contentType}${contentLength ? `, ${contentLength} bytes` : ""}]`;
    } else if (contentLength && contentLength > 100000) {
        body = `[响应体过大未读取: ${contentLength} bytes]`;
    } else {
        try {
            body = truncate(await response.clone().text());
        } catch (error) {
            body = `[读取响应体失败: ${error?.message || error}]`;
        }
    }

    return {
        status: response.status,
        statusText: response.statusText,
        headers,
        body,
        type: response.type,
        redirected: response.redirected,
        url: response.url,
    };
}

function commonEnvProperties() {
    return {
        navigator_online: typeof navigator !== "undefined" ? navigator.onLine : null,
        page_url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        connection_type:
            typeof navigator !== "undefined" && navigator.connection ? navigator.connection.effectiveType : null,
    };
}

// 全局 fetch 封装：浏览器的 window.onerror 无法捕获跨域 / 网络断开导致的 fetch 失败
// （这类错误没有响应体，reject 出的是 TypeError: Failed to fetch），也无法捕获
// "请求成功发出但服务端返回了 4xx/5xx"这类不会 reject 的错误。这里统一拦截两类情况，
// 打包尽可能详细的上下文（请求方法/头/体、响应状态/头/体、耗时、是否跨域等）后上报 PostHog，
// 同时把原始 response / 错误继续原样返回 / 抛出，保证各处 API 封装既有逻辑不受影响。
function installFetchInterceptor() {
    if (typeof window === "undefined" || typeof window.fetch !== "function") {
        return;
    }
    if (window.fetch.__posthogWrapped) {
        return;
    }

    const originalFetch = window.fetch.bind(window);

    const wrappedFetch = async (...args) => {
        const [input, init] = args;
        const requestMeta = getRequestMeta(input, init);
        const crossOrigin = isCrossOrigin(requestMeta.url);
        const startedAt = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
        const elapsed = () =>
            Math.round((typeof performance !== "undefined" && performance.now ? performance.now() : Date.now()) - startedAt);

        let response;
        try {
            response = await originalFetch(...args);
        } catch (error) {
            const dedupeKey = `reject:${requestMeta.method}:${requestMeta.url}`;
            if (shouldReport(dedupeKey)) {
                const timingHint = getResourceTimingHint(requestMeta.url);
                safeCaptureException(error, {
                    $exception_source: "fetch_interceptor",
                    error_type: "network_or_cors",
                    request_url: requestMeta.url,
                    request_method: requestMeta.method,
                    request_headers: requestMeta.headers,
                    request_body: requestMeta.body,
                    request_credentials: requestMeta.credentials,
                    request_mode: requestMeta.mode,
                    request_cross_origin: crossOrigin,
                    duration_ms: elapsed(),
                    error_message: error?.message || String(error),
                    error_name: error?.name || null,
                    // 浏览器对被 CORS 拦截的请求刻意隐藏真实状态码；resource timing 是唯一可能挖到线索的地方
                    // （前提是服务端响应带了 Timing-Allow-Origin，否则以下字段会是 null）。
                    resource_timing_hint: timingHint,
                    likely_cause: !navigator?.onLine
                        ? "设备当前离线"
                        : crossOrigin
                          ? "很可能是 CORS 预检失败或服务端未返回 Access-Control-Allow-Origin（浏览器出于安全限制隐藏了真实响应状态码/响应头）"
                          : "网络请求失败（DNS 解析失败 / 连接被拒绝 / 超时 / 被浏览器扩展拦截等）",
                    ...commonEnvProperties(),
                });
            }
            throw error;
        }

        if (!response.ok) {
            const dedupeKey = `status:${requestMeta.method}:${requestMeta.url}:${response.status}`;
            if (shouldReport(dedupeKey)) {
                const responseDetails = await buildResponseDetails(response);
                const errorType = classifyHttpError(response.status);
                const syntheticError = new Error(
                    `HTTP ${response.status} ${response.statusText || ""} — ${requestMeta.method} ${requestMeta.url}`.trim()
                );
                syntheticError.name = "HttpResponseError";
                safeCaptureException(syntheticError, {
                    $exception_source: "fetch_interceptor",
                    error_type: errorType,
                    request_url: requestMeta.url,
                    request_method: requestMeta.method,
                    request_headers: requestMeta.headers,
                    request_body: requestMeta.body,
                    request_credentials: requestMeta.credentials,
                    request_mode: requestMeta.mode,
                    request_cross_origin: crossOrigin,
                    response_status: responseDetails.status,
                    response_status_text: responseDetails.statusText,
                    response_headers: responseDetails.headers,
                    response_body: responseDetails.body,
                    response_type: responseDetails.type,
                    response_redirected: responseDetails.redirected,
                    response_url: responseDetails.url,
                    duration_ms: elapsed(),
                    likely_cause:
                        response.status === 403
                            ? "403：请检查是否为鉴权失败（token 过期/缺失），也可能是服务端 CORS 白名单未包含当前 Origin 导致业务逻辑判定为禁止访问"
                            : undefined,
                    ...commonEnvProperties(),
                });
            }
        }

        return response;
    };

    wrappedFetch.__posthogWrapped = true;
    window.fetch = wrappedFetch;
}

// 劫持 console.error：代码里被 try/catch 后主动 console.error 打印的错误，
// 也同步上报到 PostHog，且不改变原生控制台的红色报错输出。
function installConsoleErrorHook() {
    if (typeof console === "undefined" || typeof console.error !== "function") {
        return;
    }
    if (console.error.__posthogWrapped) {
        return;
    }

    const originalError = console.error.bind(console);

    const wrappedError = (...args) => {
        // 先保留原始行为：正常在控制台打印红色报错。
        originalError(...args);

        try {
            // 优先使用参数中的真实 Error 对象，保留堆栈；否则拼成一条消息。
            const errorArg = args.find((arg) => arg instanceof Error);
            if (errorArg) {
                safeCaptureException(errorArg, {
                    $exception_source: "console_error",
                });
            } else {
                const message = args
                    .map((arg) => {
                        if (typeof arg === "string") {
                            return arg;
                        }
                        try {
                            return JSON.stringify(arg);
                        } catch {
                            return String(arg);
                        }
                    })
                    .join(" ");
                if (message.trim()) {
                    const syntheticError = new Error(message);
                    syntheticError.name = "ConsoleError";
                    safeCaptureException(syntheticError, {
                        $exception_source: "console_error",
                    });
                }
            }
        } catch {
            // 劫持逻辑本身出错时静默，绝不影响 console.error 的原生行为。
        }
    };

    wrappedError.__posthogWrapped = true;
    console.error = wrappedError;
}

export function installErrorMonitoring() {
    if (installed || !posthogEnabled) {
        return;
    }
    installed = true;
    installFetchInterceptor();
    installConsoleErrorHook();
}

export default installErrorMonitoring;
